'use client';

import { SectionHeader } from '@/components/ui';
import { cssVars } from '@/styles/theme';
import { ArrowRight, Shield, Star, TrendingUp, Users, Zap } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';

export default function ForSuppliersPage() {
  const t = useTranslations('pages.forSuppliers');
  const locale = useLocale();

  const benefits = [
    { icon: Users, key: 'benefits.accessClients' },
    { icon: TrendingUp, key: 'benefits.increaseIncome' },
    { icon: Shield, key: 'benefits.protectRights' },
    { icon: Zap, key: 'benefits.easyManagement' },
  ];

  const steps = [
    { number: 1, key: 'steps.register' },
    { number: 2, key: 'steps.addServices' },
    { number: 3, key: 'steps.receiveRequests' },
    { number: 4, key: 'steps.startWorking' },
  ];

  const testimonials = [
    {
      name: t('testimonials.testimonial1.name'),
      role: t('testimonials.testimonial1.role'),
      avatar: 'https://i.pravatar.cc/150?img=1',
      rating: 5,
      textKey: 'testimonials.testimonial1.text',
    },
    {
      name: t('testimonials.testimonial2.name'),
      role: t('testimonials.testimonial2.role'),
      avatar: 'https://i.pravatar.cc/150?img=5',
      rating: 5,
      textKey: 'testimonials.testimonial2.text',
    },
    {
      name: t('testimonials.testimonial3.name'),
      role: t('testimonials.testimonial3.role'),
      avatar: 'https://i.pravatar.cc/150?img=3',
      rating: 5,
      textKey: 'testimonials.testimonial3.text',
    },
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: cssVars.neutral.bg,
      }}
    >
      {/* Hero Section */}
      <div
        className="relative overflow-hidden py-20"
        style={{
          background: `linear-gradient(135deg, ${cssVars.primary.DEFAULT} 0%, ${cssVars.accent.primary} 100%)`,
        }}
      >
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-5xl font-bold text-white">{t('hero.title')}</h1>
            <p className="mb-8 text-xl text-white/90">{t('hero.subtitle')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                href={`/${locale}/register?type=supplier`}
                className="rounded-xl px-8 py-4 text-lg font-bold transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: 'white',
                  color: cssVars.primary.DEFAULT,
                }}
              >
                {t('hero.registerButton')}
              </Link>
              <Link
                href={`/${locale}/pricing`}
                className="rounded-xl border-2 border-white px-8 py-4 text-lg font-bold text-white transition-all hover:bg-white/10"
              >
                {t('hero.viewPricingButton')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            badge={t('benefits.badge')}
            title={t('benefits.title')}
            subtitle={t('benefits.subtitle')}
            variant="light"
            align="center"
            badgeColor="warm"
            className="mx-auto mb-16 max-w-3xl"
          />

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="rounded-3xl border-2 p-6 text-center transition-all hover:shadow-lg"
                  style={{
                    backgroundColor: cssVars.neutral.surface,
                    borderColor: cssVars.neutral.border,
                  }}
                >
                  <div
                    className="mx-auto mb-4 inline-flex rounded-2xl p-4"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${cssVars.primary.DEFAULT} 15%, transparent)`,
                    }}
                  >
                    <Icon className="h-8 w-8" style={{ color: cssVars.primary.DEFAULT }} />
                  </div>
                  <h3
                    className="mb-2 text-xl font-bold"
                    style={{ color: cssVars.secondary.DEFAULT }}
                  >
                    {t(`${benefit.key}.title`)}
                  </h3>
                  <p style={{ color: cssVars.neutral.textSecondary }}>
                    {t(`${benefit.key}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20" style={{ backgroundColor: cssVars.neutral.surface }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            badge={t('steps.badge')}
            title={t('steps.title')}
            variant="light"
            align="center"
            badgeColor="warm"
            className="mx-auto mb-16 max-w-3xl"
          />

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center">
                <div
                  className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full text-2xl font-bold text-white"
                  style={{ backgroundColor: cssVars.primary.DEFAULT }}
                >
                  {step.number}
                </div>
                <h3 className="mb-2 text-lg font-bold" style={{ color: cssVars.secondary.DEFAULT }}>
                  {t(`${step.key}.title`)}
                </h3>
                <p className="text-sm" style={{ color: cssVars.neutral.textSecondary }}>
                  {t(`${step.key}.description`)}
                </p>
                {index < steps.length - 1 && (
                  <ArrowRight
                    className="absolute start-full top-8 hidden h-6 w-6 -translate-x-1/2 lg:block"
                    style={{ color: cssVars.neutral.border }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            badge={t('testimonials.badge')}
            title={t('testimonials.title')}
            variant="light"
            align="center"
            badgeColor="warm"
            className="mx-auto mb-16 max-w-3xl"
          />

          <div className="grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="rounded-3xl border-2 p-6"
                style={{
                  backgroundColor: cssVars.neutral.surface,
                  borderColor: cssVars.neutral.border,
                }}
              >
                <div className="mb-4 flex gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 fill-current"
                      style={{ color: cssVars.accent.warm }}
                    />
                  ))}
                </div>
                <p className="mb-6 leading-relaxed" style={{ color: cssVars.neutral.text }}>
                  "{t(testimonial.textKey)}"
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold" style={{ color: cssVars.secondary.DEFAULT }}>
                      {testimonial.name}
                    </div>
                    <div className="text-sm" style={{ color: cssVars.neutral.textSecondary }}>
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div
        className="py-20"
        style={{
          background: `linear-gradient(135deg, ${cssVars.primary.DEFAULT} 0%, ${cssVars.accent.primary} 100%)`,
        }}
      >
        <div className="container mx-auto px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 text-4xl font-bold text-white">{t('cta.title')}</h2>
          <p className="mb-8 text-xl text-white/90">{t('cta.subtitle')}</p>
          <Link
            href={`/${locale}/register?type=supplier`}
            className="inline-block rounded-xl px-8 py-4 text-lg font-bold transition-opacity hover:opacity-90"
            style={{
              backgroundColor: 'white',
              color: cssVars.primary.DEFAULT,
            }}
          >
            {t('cta.button')}
          </Link>
        </div>
      </div>
    </div>
  );
}
